// ---------------------------------------------------------------------------
// PDF export for the Hours Breakdown report's Work Type view.
//
// The chart is DRAWN, not screenshotted. The R&D export captures its charts with
// html2canvas, which is the right call there because those panels are elaborate;
// here the chart is a donut of four numbers, and drawing it directly gives
// vector output that stays sharp at any zoom or print size, needs nothing from
// the DOM, and cannot be caught mid-render. The palette is imported from the
// on-screen chart so the two cannot drift.
//
// Deliberately just the highlights someone typed in, the chart, its share bars
// and the names of the TimeCamp tasks behind them: this export is the summary,
// and every caveat, drill-down and detail stays on screen where it can be
// explored.
//
// And deliberately no hours anywhere on the page. This is a share-of-work
// document - what the time went on, in proportion. Hours are still taken as
// input because the slice angles have to come from the exact figures rather
// than from already-rounded percentages, but they are never printed.
//
// Part of the one-off Hours Breakdown module - delete with the page.
// ---------------------------------------------------------------------------

import { jsPDF } from 'jspdf';
import { CATEGORICAL } from '../components/rndCredits/PiePanel';

const num = (value, decimals = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return String(Math.round((n + Number.EPSILON) * 10 ** decimals) / 10 ** decimals);
};

const pct = (value) => `${num(value, 1)}%`;

const hexToRgb = (hex) => {
  const clean = String(hex || '').replace('#', '');
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
};

const WHITE_INK = [255, 255, 255];
const DARK_INK = [26, 26, 26];

// WCAG relative luminance.
const luminanceOf = (hex) => {
  const [r, g, b] = hexToRgb(hex).map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Which ink stays readable on a given fill.
//
// A label sitting on a coloured mark has to contrast with the mark, so it takes
// white or near-black rather than a text colour - the one place here a figure is
// not in ordinary ink. Rather than a luminance threshold, both are scored and
// the better one wins: the palette runs from a dark blue to a light amber, and
// any single cutoff picks white for the pink, where it lands at 2.7:1. Scoring
// also means a future palette change cannot quietly produce an unreadable label.
const labelInkFor = (hex) => {
  const l = luminanceOf(hex);
  const against = (ink) => {
    const il = luminanceOf(`#${ink.map((c) => c.toString(16).padStart(2, '0')).join('')}`);
    const [hi, lo] = il > l ? [il, l] : [l, il];
    return (hi + 0.05) / (lo + 0.05);
  };
  return against(WHITE_INK) >= against(DARK_INK) ? WHITE_INK : DARK_INK;
};

// Below this a slice is too narrow to hold its own figure without the text
// spilling over its edges. Those shares are not lost - every one of them is in
// the share bars beside the chart, which is also why
// labelling every slice regardless would be noise rather than information.
const MIN_LABELLED_SHARE = 5;

// A donut slice as a filled polygon.
//
// jsPDF has no arc primitive, so each slice is approximated by walking the outer
// edge in small steps and the inner edge back. At 3 degrees a step the flat
// spots are far below what any printer resolves, and the whole thing stays
// vector rather than becoming a bitmap.
const ARC_STEP_DEGREES = 3;

const drawSlice = (doc, { cx, cy, outer, inner, startDeg, endDeg, color }) => {
  const point = (radius, deg) => {
    const a = ((deg - 90) * Math.PI) / 180;
    return [cx + radius * Math.cos(a), cy + radius * Math.sin(a)];
  };

  const steps = Math.max(2, Math.ceil((endDeg - startDeg) / ARC_STEP_DEGREES));
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    points.push(point(outer, startDeg + ((endDeg - startDeg) * i) / steps));
  }
  for (let i = steps; i >= 0; i -= 1) {
    points.push(point(inner, startDeg + ((endDeg - startDeg) * i) / steps));
  }

  const [r, g, b] = hexToRgb(color);
  doc.setFillColor(r, g, b);
  // doc.lines() takes deltas from the starting point, so the absolute polygon is
  // converted to relative hops; `true` closes it and 'F' fills it.
  const deltas = points.slice(1).map((p, i) => [p[0] - points[i][0], p[1] - points[i][1]]);
  doc.lines(deltas, points[0][0], points[0][1], [1, 1], 'F', true);
};

// TimeCamp task paths shown under a friendlier name in the export.
const TASK_LABEL_RENAMES = {
  'z Resources / Admin': 'Tech Admin',
};
const taskLabelFor = (label) => TASK_LABEL_RENAMES[String(label || '').trim()] || label;

// Style-guide tokens (style-guide/tokens.css), as RGB for jsPDF.
const INK = [25, 28, 30];
const INK_MUTED = [69, 71, 76];
const INK_FAINT = [117, 119, 125];
const SURFACE_SUNK = [236, 238, 240];
const BORDER = [224, 227, 229];
const ACCENT = [0, 115, 116];
const ACCENT_ON_DARK = [53, 167, 168];
const MUTED_ON_DARK = [164, 169, 179];
const NO_SHARE_COLOR = '#a9a8a2';
const EMPTY_RING_COLOR = '#eceef0';

// Clip a string to one line of `width`, with an ellipsis if it had to be cut.
const fitText = (doc, text, width) => {
  const value = String(text || '');
  if (doc.getTextWidth(value) <= width) return value;
  let cut = value;
  while (cut.length > 1 && doc.getTextWidth(`${cut}...`) > width) cut = cut.slice(0, -1);
  return `${cut.trimEnd()}...`;
};

// Small uppercase caption, letter-spaced, used for every label above a value.
const CAPTION_CHAR_SPACE = 0.8;
const caption = (doc, text, x, y, { color = INK_FAINT, align = 'left' } = {}) => {
  const value = String(text).toUpperCase();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...color);
  // jsPDF right-aligns on the width without the letter spacing, so a spaced
  // caption would overhang its x by the spacing; pull it back by that much.
  const spacing = align === 'right' ? CAPTION_CHAR_SPACE * (value.length - 1) : 0;
  doc.text(value, x - spacing, y, { align, charSpace: CAPTION_CHAR_SPACE });
};

// Section heading with a short accent tick above it.
const sectionHeading = (doc, text, x, y) => {
  doc.setFillColor(...ACCENT);
  doc.rect(x, y - 16, 18, 2.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...INK);
  doc.text(text, x, y);
};

/**
 * `rows`        the four work-type rows: { key, label, hours, entries, share }
 * `tasks`       the counted TimeCamp tasks: { label, hours, share }
 * `highlights`  free-text bullet points, already split into one string each
 * `meta`        { employeeName, periodLabel, start, end }
 */
export const exportWorkTypePdf = ({ rows = [], tasks = [], highlights = [], meta = {} }) => {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 48;
  const contentWidth = pageWidth - marginX * 2;

  const when = String(meta.periodLabel || `${meta.start || ''} to ${meta.end || ''}`).trim();
  const generated = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const totalHours = rows.reduce((sum, r) => sum + (Number(r.hours) || 0), 0);
  const FOOTER_TOP = pageHeight - 56;

  /*
    Biggest first, then coloured - in that order, and not the other way round.

    PiePanel sorts by value before it indexes into CATEGORICAL, so the colour a
    work type gets on screen is decided by where it ranks, not by where it sits
    in the bucket definitions. `rows` arrives in definition order. Colouring it
    as it stands hands the same work type one colour in the page and a different
    one in the PDF of that page, which is exactly what sharing the palette was
    meant to prevent.

    No "Other" fold to mirror: the panel keeps five slices and this chart has at
    most four categories, unclassified work being counted elsewhere rather than
    drawn. Add a fifth bucket and that stops being true for both of them.
  */
  const ranked = [...rows]
    .sort((a, b) => (Number(b.hours) || 0) - (Number(a.hours) || 0))
    .map((r, i) => ({
      ...r,
      color: (Number(r.hours) || 0) > 0 ? CATEGORICAL[i % CATEGORICAL.length] : NO_SHARE_COLOR,
    }));
  const withColor = ranked.filter((r) => (Number(r.hours) || 0) > 0);
  const top = withColor[0];

  // --- Header band ---------------------------------------------------------
  //
  // A dark band carrying the title, closed off by a thin accent stripe. Dark
  // neutral rather than accent-filled: the style guide keeps the accent off
  // large areas, and the stripe is enough to tie the page to the app.
  const BAND_H = 92;
  doc.setFillColor(...INK);
  doc.rect(0, 0, pageWidth, BAND_H, 'F');
  doc.setFillColor(...ACCENT);
  doc.rect(0, BAND_H, pageWidth, 3, 'F');

  caption(doc, 'Hours Breakdown Report', marginX, 34, { color: ACCENT_ON_DARK });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(255, 255, 255);
  doc.text('Tech and Tools Breakdown', marginX, 60);

  caption(doc, 'Period', pageWidth - marginX, 34, { color: MUTED_ON_DARK, align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text(fitText(doc, when, 200), pageWidth - marginX, 49, { align: 'right' });

  let y = BAND_H + 36;

  // --- Highlights --------------------------------------------------------
  //
  // Whatever the person exporting wanted said up front, one bullet per point.
  // Left out entirely when there is nothing to say rather than drawn empty.
  const points = highlights.map((h) => String(h || '').trim()).filter(Boolean);
  if (points.length > 0) {
    sectionHeading(doc, 'Highlights', marginX, y);
    y += 24;

    const LINE_H = 14;
    const textX = marginX + 14;
    const textW = contentWidth - 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    for (const point of points) {
      const lines = doc.splitTextToSize(point, textW);
      if (y + lines.length * LINE_H > FOOTER_TOP) {
        doc.addPage();
        y = 64;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
      }
      doc.setFillColor(...ACCENT);
      doc.circle(marginX + 3, y - 3.5, 2.2, 'F');
      doc.setTextColor(...INK);
      doc.text(lines, textX, y, { lineHeightFactor: LINE_H / 10 });
      y += lines.length * LINE_H + 6;
    }
    y += 38;
  }

  // The chart block is drawn as one piece, so it moves to a fresh page rather
  // than being cut by the footer when the highlights run long.
  if (y + 26 + 2 * 92 + 16 > FOOTER_TOP) {
    doc.addPage();
    y = 64;
  }

  // --- Donut + share bars --------------------------------------------------
  sectionHeading(doc, 'Distribution', marginX, y);

  const outer = 92;
  const inner = 58;
  const cx = marginX + outer + 8;
  const cy = y + 26 + outer;

  const slicePlacements = [];
  const boundaries = [];

  if (withColor.length <= 1) {
    // A single category is a ring, not a slice - a 360 degree polygon would
    // close across the middle and fill the hole. No categories draws the same
    // ring in grey, so the page keeps its shape.
    const color = top ? top.color : EMPTY_RING_COLOR;
    drawSlice(doc, { cx, cy, outer, inner, startDeg: 0, endDeg: 180, color });
    drawSlice(doc, { cx, cy, outer, inner, startDeg: 180, endDeg: 360, color });
  } else {
    let cursor = 0;
    for (const row of withColor) {
      const startDeg = (cursor / totalHours) * 360;
      cursor += Number(row.hours) || 0;
      const endDeg = (cursor / totalHours) * 360;
      drawSlice(doc, { cx, cy, outer, inner, startDeg, endDeg, color: row.color });
      slicePlacements.push({ row, midDeg: (startDeg + endDeg) / 2 });
      boundaries.push(startDeg);
    }
  }

  // White gaps between slices, so neighbouring colours read as separate marks.
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(2);
  for (const deg of boundaries) {
    const a = ((deg - 90) * Math.PI) / 180;
    doc.line(
      cx + (inner - 1) * Math.cos(a),
      cy + (inner - 1) * Math.sin(a),
      cx + (outer + 1) * Math.cos(a),
      cy + (outer + 1) * Math.sin(a)
    );
  }

  // Figures on the slices, drawn after every fill so none is painted over.
  const bandRadius = (outer + inner) / 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  for (const { row, midDeg } of slicePlacements) {
    if ((Number(row.share) || 0) < MIN_LABELLED_SHARE) continue;
    const a = ((midDeg - 90) * Math.PI) / 180;
    doc.setTextColor(...labelInkFor(row.color));
    // +3 nudges the baseline down so the text sits on the band's centre line.
    doc.text(pct(row.share), cx + bandRadius * Math.cos(a), cy + bandRadius * Math.sin(a) + 3, {
      align: 'center',
    });
  }

  // The hole stays empty - every slice is treated the same - unless there is
  // nothing to draw.
  if (!top) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...INK_FAINT);
    doc.text('No data', cx, cy + 3, { align: 'center' });
  }

  // Share bars beside the donut: label and figure above a track filled to the
  // share of the whole, so the proportions can be compared on a straight line
  // as well as round the ring.
  const barX = cx + outer + 44;
  const barW = pageWidth - marginX - barX;
  const ROW_H = 40;
  const listH = Math.max(withColor.length, 1) * ROW_H;
  let barY = cy - listH / 2 + 12;

  if (withColor.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...INK_FAINT);
    doc.text('Nothing was classified for this period.', barX, barY + 6);
  }
  for (const row of withColor) {
    doc.setFillColor(...hexToRgb(row.color));
    doc.roundedRect(barX, barY - 7.5, 9, 9, 2, 2, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...INK);
    doc.text(pct(row.share), barX + barW, barY, { align: 'right' });
    const shareWidth = doc.getTextWidth(pct(row.share));

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...INK_MUTED);
    doc.text(fitText(doc, row.label, barW - 16 - shareWidth - 12), barX + 16, barY);

    const trackY = barY + 9;
    doc.setFillColor(...SURFACE_SUNK);
    doc.roundedRect(barX, trackY, barW, 6, 3, 3, 'F');
    const fillW = Math.max(6, (barW * Math.min(Number(row.share) || 0, 100)) / 100);
    doc.setFillColor(...hexToRgb(row.color));
    doc.roundedRect(barX, trackY, fillW, 6, 3, 3, 'F');

    barY += ROW_H;
  }

  // --- TimeCamp tasks ------------------------------------------------------
  //
  // Every task the report counted, biggest first - names only, in two columns.
  // The shares live in the chart above; here the list just says what the time
  // was spent on. It can run long, so it breaks onto new pages; the footer loop
  // below numbers them all.
  y = Math.max(cy + outer, barY) + 48;
  const TASK_ROW_H = 22;
  const COL_GAP = 24;
  const colW = (contentWidth - COL_GAP) / 2;
  const taskRows = [...tasks]
    .sort((a, b) => (Number(b.hours) || 0) - (Number(a.hours) || 0))
    .map((t) => taskLabelFor(t.label));

  if (y + 40 > FOOTER_TOP) {
    doc.addPage();
    y = 64;
  }
  sectionHeading(doc, 'TimeCamp tasks', marginX, y);
  y += 24;

  if (taskRows.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...INK_FAINT);
    doc.text('No tasks were counted for this period.', marginX, y);
  }

  // Filled row by row, left then right, so reading order stays biggest first.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  for (let i = 0; i < taskRows.length; i += 2) {
    if (y + TASK_ROW_H > FOOTER_TOP) {
      doc.addPage();
      y = 64;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
    }
    for (let col = 0; col < 2 && i + col < taskRows.length; col += 1) {
      const x = marginX + col * (colW + COL_GAP);
      doc.setFillColor(...ACCENT);
      doc.circle(x + 3, y - 3, 2, 'F');
      doc.setTextColor(...INK_MUTED);
      doc.text(fitText(doc, taskRows[i + col], colW - 14), x + 12, y);
    }
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.5);
    doc.line(marginX, y + 8, pageWidth - marginX, y + 8);
    y += TASK_ROW_H;
  }

  // --- Footer --------------------------------------------------------------
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.6);
    doc.line(marginX, pageHeight - 40, pageWidth - marginX, pageHeight - 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...INK_FAINT);
    doc.text(
      fitText(doc, `Tech and Tools Breakdown  |  Generated ${generated}`, contentWidth - 80),
      marginX,
      pageHeight - 26
    );
    doc.text(`Page ${i} of ${pages}`, pageWidth - marginX, pageHeight - 26, { align: 'right' });
  }

  const slug = String(meta.employeeName || 'employee').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  doc.save(`tech-and-tools_${slug}_${meta.start || 'start'}_to_${meta.end || 'end'}.pdf`);
};

export default exportWorkTypePdf;
